/* @jsx h */
import { LemmonModal } from "@app/vue/components/index";
import { defineComponent, ref, h } from "vue";
import { api } from "@app/data/index";
import { FloorPlan } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast } from "@app/vue/providers/Toast";

// todo: add delete button and call api api.deleteFloorPlan

export const AddFloorPlanModal = defineComponent<{
  onClose: () => void;
  onSave?: (floorPlan: FloorPlan) => void;
  onDelete: () => void;
  editingFloorPlan?: FloorPlan;
}>({
  name: "AddFloorPlanModal",
  props: ["onClose", "onSave", "editingFloorPlan"],
  setup(props) {
    const name = ref(props.editingFloorPlan?.name || "");
    const rank = ref(props.editingFloorPlan?.rank || 0);
    const selectedPreset = ref<
      "9x5" | "8x5" | "12x8" | "16x10" | "20x12" | "custom"
    >("9x5");
    const customRows = ref(props.editingFloorPlan?.rows || 5);
    const customCols = ref(props.editingFloorPlan?.columns || 9);
    const selectedColor = ref(props.editingFloorPlan?.color || "");
    const saving = ref(false);
    const deleting = ref(false);
    const { t } = useLocalization();
    const toast = useToast();

    // Set initial preset based on editing floor plan
    if (props.editingFloorPlan) {
      const { rows, columns } = props.editingFloorPlan;
      if (rows === 5 && columns === 9) {
        selectedPreset.value = "9x5";
      } else if (rows === 5 && columns === 8) {
        selectedPreset.value = "8x5";
      } else if (rows === 8 && columns === 12) {
        selectedPreset.value = "12x8";
      } else if (rows === 10 && columns === 16) {
        selectedPreset.value = "16x10";
      } else if (rows === 12 && columns === 20) {
        selectedPreset.value = "20x12";
      } else {
        selectedPreset.value = "custom";
      }
    }

    const adjustCustomValue = (type: "rows" | "cols", delta: number) => {
      if (type === "rows") {
        customRows.value = Math.max(1, Math.min(40, customRows.value + delta));
      } else {
        customCols.value = Math.max(1, Math.min(40, customCols.value + delta));
      }
    };

    const handleDelete = async () => {
      if (!props.editingFloorPlan) return;

      try {
        deleting.value = true;
        await api.deleteFloorPlan(props.editingFloorPlan.id.toString());
        props.onDelete();
        props.onClose();
      } catch (error) {
        console.error("Failed to delete floor plan:", error);
        toast.showToast("Failed to delete floor plan. Please try again.", {
          type: "error",
        });
      } finally {
        deleting.value = false;
      }
    };

    const handleSave = async () => {
      let rows, cols;

      if (selectedPreset.value === "9x5") {
        cols = 9;
        rows = 5;
      } else if (selectedPreset.value === "8x5") {
        cols = 8;
        rows = 5;
      } else if (selectedPreset.value === "12x8") {
        cols = 12;
        rows = 8;
      } else if (selectedPreset.value === "16x10") {
        cols = 16;
        rows = 10;
      } else if (selectedPreset.value === "20x12") {
        cols = 20;
        rows = 12;
      } else {
        rows = customRows.value;
        cols = customCols.value;
      }

      const floorPlanData = {
        name: name.value,
        rank: rank.value,
        rows,
        columns: cols,
        color: selectedColor.value || undefined,
        plan: props.editingFloorPlan?.plan || [],
      };

      try {
        saving.value = true;
        let result;

        if (props.editingFloorPlan) {
          // Update existing floor plan
          result = await api.updateFloorPlan(props.editingFloorPlan.id, {
            ...floorPlanData,
            id: props.editingFloorPlan.id,
          });
        } else {
          // Create new floor plan
          result = await api.createFloorPlan(floorPlanData);
        }

        if (props.onSave) {
          props.onSave(result);
        }
        props.onClose();
      } catch (error) {
        console.error("Failed to save floor plan:", error);
        toast.showToast("Failed to save floor plan. Please try again.", {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={props.editingFloorPlan ? "Edit Floor Plan" : "Add Floor Plan"}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="p-4 border-t ">
            <div class={`flex flex-1 gap-2`}>
              <div class="flex flex-1">
                {props.editingFloorPlan && (
                  <button
                    onClick={handleDelete}
                    class="btn btn-error"
                    disabled={deleting.value || saving.value}
                  >
                    {deleting.value ? (
                      <span class="loading loading-spinner loading-xs"></span>
                    ) : (
                      <>
                        <span class="material-symbols-outlined mr-1">
                          delete
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <button onClick={props.onClose} class="btn btn-neutral">
                {t("general.words.cancel")}
              </button>
              <button
                onClick={handleSave}
                class="btn btn-primary"
                disabled={!name.value.trim() || saving.value || deleting.value}
              >
                {saving.value ? (
                  <span class="loading loading-spinner loading-xs"></span>
                ) : (
                  t("general.words.save")
                )}
              </button>
            </div>
          </div>
        )}
      >
        <div class="p-4 space-y-6">
          <div>
            <label class="block text-sm font-medium  mb-2">
              Floor Plan Name
            </label>
            <input
              type="text"
              class="input input-bordered w-full"
              placeholder="Enter floor plan name"
              value={name.value}
              onInput={(e) =>
                (name.value = (e.target as HTMLInputElement).value)
              }
            />
          </div>

          <div>
            <label class="block text-sm font-medium  mb-2">
              Rank (for sorting)
            </label>
            <input
              type="number"
              class="input input-bordered w-full"
              placeholder="Enter rank number"
              value={rank.value}
              onInput={(e) =>
                (rank.value = Number((e.target as HTMLInputElement).value))
              }
            />
          </div>

          <div>
            <label class="block text-sm font-medium  mb-2">Room Color</label>
            <div class="flex items-center gap-2 flex-wrap">
              {[
                {
                  value: "",
                  label: "Default",
                  color: "var(--floor-plan-background)",
                },
                { value: "#1e3a5f", label: "Navy", color: "#1e3a5f" },
                { value: "#1e3f25", label: "Forest", color: "#1e3f25" },
                { value: "#4a2222", label: "Wine", color: "#4a2222" },
                { value: "#3d2e1a", label: "Walnut", color: "#3d2e1a" },
                { value: "#2d3040", label: "Slate", color: "#2d3040" },
                { value: "#3a2e1a", label: "Warm", color: "#3a2e1a" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => (selectedColor.value = opt.value)}
                  class="flex flex-col items-center gap-1"
                  title={opt.label}
                >
                  <div
                    class="w-8 h-8 rounded-lg border-2 transition-all"
                    style={{
                      background: opt.color,
                      borderColor:
                        selectedColor.value === opt.value
                          ? "#6366f1"
                          : "rgba(255,255,255,0.1)",
                      boxShadow:
                        selectedColor.value === opt.value
                          ? "0 0 0 2px rgba(99,102,241,0.3)"
                          : "none",
                    }}
                  />
                  <span class="text-[10px] ">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium  mb-3">Dimensions</label>

            <div class="space-y-3">
              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="9x5"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "9x5"}
                  onChange={() => (selectedPreset.value = "9x5")}
                />
                <label for="9x5" class="">
                  9 columns × 5 rows
                </label>
              </div>

              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="8x5"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "8x5"}
                  onChange={() => (selectedPreset.value = "8x5")}
                />
                <label for="8x5" class="">
                  8 × 5 <span class=" text-xs ml-1">Standard</span>
                </label>
              </div>

              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="12x8"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "12x8"}
                  onChange={() => (selectedPreset.value = "12x8")}
                />
                <label for="12x8" class="">
                  12 × 8 <span class=" text-xs ml-1">Medium</span>
                </label>
              </div>

              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="16x10"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "16x10"}
                  onChange={() => (selectedPreset.value = "16x10")}
                />
                <label for="16x10" class="">
                  16 × 10 <span class=" text-xs ml-1">Large</span>
                </label>
              </div>

              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="20x12"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "20x12"}
                  onChange={() => (selectedPreset.value = "20x12")}
                />
                <label for="20x12" class="">
                  20 × 12 <span class=" text-xs ml-1">Extra Large</span>
                </label>
              </div>

              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  id="custom"
                  name="preset"
                  class="radio radio-sm"
                  checked={selectedPreset.value === "custom"}
                  onChange={() => (selectedPreset.value = "custom")}
                />
                <label for="custom" class="text-accent">
                  Custom
                </label>
              </div>

              {selectedPreset.value === "custom" && (
                <div class="ml-6 space-y-3 border-l-2 border-gray-600 pl-4">
                  <div class="flex items-center space-x-4">
                    <label class=" w-16">Rows:</label>
                    <div class="flex items-center space-x-2">
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm btn-circle"
                        onClick={() => adjustCustomValue("rows", -1)}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        class="input input-bordered input-sm w-16 text-center"
                        value={customRows.value}
                        min="1"
                        max="20"
                        onInput={(e) => {
                          const val = parseInt(
                            (e.target as HTMLInputElement).value,
                          );
                          if (!isNaN(val))
                            customRows.value = Math.max(1, Math.min(40, val));
                        }}
                      />
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm btn-circle"
                        onClick={() => adjustCustomValue("rows", 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div class="flex items-center space-x-4">
                    <label class=" w-16">Columns:</label>
                    <div class="flex items-center space-x-2">
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm btn-circle"
                        onClick={() => adjustCustomValue("cols", -1)}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        class="input input-bordered input-sm w-16 text-center"
                        value={customCols.value}
                        min="1"
                        max="20"
                        onInput={(e) => {
                          const val = parseInt(
                            (e.target as HTMLInputElement).value,
                          );
                          if (!isNaN(val))
                            customCols.value = Math.max(1, Math.min(40, val));
                        }}
                      />
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm btn-circle"
                        onClick={() => adjustCustomValue("cols", 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
